// src/pages/TermsConditions.jsx
import React from "react";

const PAGE_GRADIENT =
  "radial-gradient(circle at top, #26266b 0, #050514 55%, #000 100%)";

export default function TermsConditions() {
  return (
    <div
      className="min-h-screen text-slate-100 px-4 py-10"
      style={{ background: PAGE_GRADIENT }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-pink-400/80 mb-2">
            Legal
          </p>
          <h1 className="text-3xl md:text-4xl font-semibold mb-2">
            Terms and Conditions
          </h1>
          <p className="text-sm text-slate-400">
            Last updated: <span className="font-medium text-slate-200">27 November 2025</span>
          </p>
        </header>

        <div className="space-y-8 text-sm md:text-base leading-relaxed text-slate-200">
          {/* 1. Intro */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              1. Overview
            </h2>
            <p className="mb-2">
              These Terms and Conditions ("Terms") govern your access to and use
              of the <span className="font-semibold">PixelAds.ai</span> website,
              app, and related services (collectively, the "Service").
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these
              Terms. If you do not agree, you should not use PixelAds.
            </p>
          </section>

          {/* 2. Eligibility */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              2. Eligibility
            </h2>
            <p className="mb-2">
              To use the Service, you must:
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>Be at least 18 years old or the age of majority in your country</li>
              <li>Have the legal capacity to enter into a binding agreement</li>
              <li>Use the Service only for lawful purposes</li>
            </ul>
          </section>

          {/* 3. Accounts */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              3. Accounts and security
            </h2>
            <p className="mb-2">
              To access most features, you must create an account and sign in
              via our authentication provider (Clerk).
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>You agree to provide accurate and up to date information.</li>
              <li>
                You are responsible for keeping your login credentials secure
                and for all activity under your account.
              </li>
              <li>
                You must notify us promptly if you suspect unauthorized access
                or any security issue.
              </li>
            </ul>
            <p className="mt-2">
              We may suspend or terminate your account if we detect abuse,
              fraud, or violations of these Terms.
            </p>
          </section>

          {/* 4. Subscriptions */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              4. Subscriptions, plans, and billing
            </h2>
            <p className="mb-2">
              PixelAds offers paid subscription plans (for example, Starter, Pro,
              Business) with specific quotas for images, videos, and product
              pages, as described on our pricing page.
            </p>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>Fees are billed in advance for each billing period.</li>
              <li>
                Subscriptions renew automatically unless cancelled before the
                renewal date.
              </li>
              <li>
                Payments and billing are handled by Stripe. You authorize us and
                Stripe to charge your selected payment method for subscription
                fees and applicable taxes.
              </li>
            </ul>
            <p>
              For details on refunds and cancellations, see our{" "}
              <span className="font-semibold">Refund Policy</span> page.
            </p>
          </section>

          {/* 5. Free trials */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              5. Free trials and promotions
            </h2>
            <p>
              From time to time, we may offer free trials or promotional
              discounts. The duration and conditions will be communicated in the
              app or on our website. We may modify or end a trial or promotion
              at any time, subject to applicable law.
            </p>
          </section>

          {/* 6. Use of the Service */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              6. Use of the Service
            </h2>
            <p className="mb-2">
              We grant you a limited, non-exclusive, non-transferable license to
              access and use PixelAds for your own ecommerce and marketing
              activities, subject to these Terms and your current plan limits.
            </p>
            <p className="mb-2">You agree not to:</p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>Reverse engineer or attempt to extract our source code</li>
              <li>Bypass or tamper with usage or quota limits</li>
              <li>
                Resell or sublicense access to the Service without our written
                permission
              </li>
              <li>
                Use PixelAds to build a competing product in a way that violates
                applicable laws or our intellectual property rights
              </li>
            </ul>
          </section>

          {/* 7. User content and AI outputs */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              7. User content and AI generated content
            </h2>
            <p className="mb-2">
              You are responsible for the content you upload or enter into the
              Service, including product data, images, videos, and prompts.
            </p>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>
                You confirm that you have all rights and permissions needed to
                use that content in PixelAds.
              </li>
              <li>
                You agree that your content does not violate any laws or third
                party rights (for example, copyright, trademarks, privacy).
              </li>
            </ul>
            <p className="mb-2">
              The Service uses AI models and third party providers (such as
              Phot.AI and TopView) to generate images, videos, and other
              outputs ("AI Content") based on your inputs.
            </p>
            <ul className="list-disc list-inside space-y-1 text-slate-200/90">
              <li>
                We give you a license to use AI Content generated in your
                account, subject to these Terms and your plan limits.
              </li>
              <li>
                AI Content may not be unique, and similar outputs may be
                produced for other users.
              </li>
              <li>
                You are responsible for reviewing AI Content before using it in
                campaigns, and for ensuring it meets platform policies and legal
                requirements.
              </li>
            </ul>
          </section>

          {/* 8. Prohibited use */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              8. Prohibited activities
            </h2>
            <p className="mb-2">
              You agree not to use PixelAds to generate, upload, or distribute
              content that is:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-3 text-slate-200/90">
              <li>Illegal or promotes illegal activities</li>
              <li>Harassing, hateful, discriminatory, or violent</li>
              <li>Sexually explicit or exploitative, especially involving minors</li>
              <li>Misleading in a way that causes harm or confusion</li>
              <li>
                Infected with malware, scripts, or any code intended to damage
                systems or data
              </li>
            </ul>
            <p>
              We may remove content, suspend, or terminate accounts that violate
              these rules or applicable laws.
            </p>
          </section>

          {/* 9. Third-party services */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              9. Third party services and integrations
            </h2>
            <p>
              PixelAds integrates with third party providers and platforms. We
              do not control these services and are not responsible for their
              content, terms, or policies. Your use of third party services is
              governed by their own terms and privacy policies.
            </p>
          </section>

          {/* 10. Intellectual property */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              10. Intellectual property
            </h2>
            <p className="mb-2">
              PixelAds, including its design, software, logos, and branding, is
              owned by us or our licensors and is protected by intellectual
              property laws.
            </p>
            <p>
              Nothing in these Terms transfers ownership of our IP to you. You
              may not use our name, logo, or trademarks without our prior
              written consent.
            </p>
          </section>

          {/* 11. Disclaimers */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              11. Disclaimers
            </h2>
            <p className="mb-2">
              The Service and AI Content are provided on an "as is" and "as
              available" basis, without warranties of any kind, whether express
              or implied.
            </p>
            <p className="mb-2">
              We do not guarantee that PixelAds will:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>Be uninterrupted, secure, or free from errors</li>
              <li>Always meet your specific performance, conversion, or revenue goals</li>
              <li>Produce AI Content that is free from inaccuracies or infringement risk</li>
            </ul>
            <p>
              You use PixelAds at your own risk and are responsible for
              verifying outputs before publishing or advertising with them.
            </p>
          </section>

          {/* 12. Limitation of liability */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              12. Limitation of liability
            </h2>
            <p className="mb-2">
              To the maximum extent permitted by law, PixelAds and its owners,
              employees, and partners will not be liable for:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>Indirect, incidental, special, or consequential damages</li>
              <li>Loss of profits, revenue, or data</li>
              <li>
                Any claim related to your use of AI Content or third party
                services
              </li>
            </ul>
            <p>
              Our total liability for any claim arising out of or related to the
              Service will not exceed the amount you paid to us in the six
              months before the event that gave rise to the claim.
            </p>
          </section>

          {/* 13. Indemnification */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              13. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless PixelAds, its owners, and
              team members from any claims, losses, or expenses (including
              reasonable legal fees) arising from your use of the Service, your
              content, your AI Content, or your violation of these Terms or
              third party rights.
            </p>
          </section>

          {/* 14. Termination */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              14. Termination
            </h2>
            <p className="mb-2">
              We may suspend or terminate your access to PixelAds at any time
              if:
            </p>
            <ul className="list-disc list-inside space-y-1 mb-2 text-slate-200/90">
              <li>You breach these Terms or applicable law</li>
              <li>We suspect abuse, fraud, or security risk</li>
              <li>We choose to discontinue all or part of the Service</li>
            </ul>
            <p>
              You may stop using PixelAds at any time and cancel your
              subscription in the app. Certain sections of these Terms will
              survive termination, including those on intellectual property,
              disclaimers, liability, and indemnification.
            </p>
          </section>

          {/* 15. Governing law */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              15. Governing law and disputes
            </h2>
            <p>
              These Terms are governed by the laws of Romania, without regard to
              conflict of law principles. Any disputes will be handled by the
              competent courts in Romania, unless mandatory law requires
              otherwise.
            </p>
          </section>

          {/* 16. Changes */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              16. Changes to these Terms
            </h2>
            <p>
              We may update these Terms from time to time. When we do, we will
              update the "Last updated" date at the top, and where appropriate,
              notify you in the app or by email. If you continue to use
              PixelAds after changes take effect, you accept the updated Terms.
            </p>
          </section>

          {/* 17. Contact */}
          <section>
            <h2 className="text-xl font-semibold mb-3 text-slate-50">
              17. Contact
            </h2>
            <p>
              If you have questions about these Terms, email{" "}
              <span className="font-mono">contact@pixelads.ai</span>.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
